// 拍照：直接调用摄像头（电脑和手机都一样），带人脸取景框和倒计时；不支持或被拒绝时退回选文件

import { useEffect, useRef, useState } from "preact/hooks";
import { Icon } from "./ui";

export function Camera({
  onShot,
  onClose,
  onFallback,
}: {
  onShot: (file: File) => void;
  onClose: () => void;
  /** 浏览器不支持摄像头时，改用系统选图 */
  onFallback: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [facing, setFacing] = useState<"user" | "environment">("user");
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timer, setTimer] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [flash, setFlash] = useState(false);
  const [multi, setMulti] = useState(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let alive = true;
    setReady(false);
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        onFallback();
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1440 } },
          audio: false,
        });
        if (!alive) {
          for (const t of stream.getTracks()) t.stop();
          return;
        }
        const v = video.current!;
        v.srcObject = stream;
        await v.play().catch(() => {});
        setReady(true);
        setError(null);
        const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
        setMulti(devices.filter((d) => d.kind === "videoinput").length > 1);
      } catch (e) {
        const name = (e as { name?: string })?.name ?? "";
        setError(
          name === "NotAllowedError" || name === "SecurityError"
            ? "没有摄像头权限。可以在浏览器地址栏的权限设置里允许摄像头，或者改用相册。"
            : name === "NotFoundError" || name === "OverconstrainedError"
              ? "没找到可用的摄像头，改用相册吧。"
              : "摄像头打开失败，改用相册吧。",
        );
      }
    })();
    return () => {
      alive = false;
      if (stream) for (const t of stream.getTracks()) t.stop();
    };
  }, [facing]);

  const capture = () => {
    const v = video.current;
    if (!v?.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    // 预览是镜像的（像照镜子），存下来的照片保持真实方向，和别人看你的样子一致
    c.getContext("2d")!.drawImage(v, 0, 0);
    setFlash(true);
    c.toBlob(
      (b) => {
        if (b) onShot(new File([b], `camera-${Date.now()}.jpg`, { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92,
    );
  };

  const shoot = () => {
    if (!ready || count !== null) return;
    if (!timer) {
      capture();
      return;
    }
    let n = 3;
    setCount(n);
    const t = setInterval(() => {
      n -= 1;
      if (n <= 0) {
        clearInterval(t);
        setCount(null);
        capture();
      } else {
        setCount(n);
      }
    }, 1000);
  };

  return (
    <div class="camera">
      <div class="camera-view">
        <video ref={video} playsInline muted class={facing === "user" ? "mirror" : ""} />
        {ready && (
          <svg class="camera-guide" viewBox="0 0 300 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
            <defs>
              <mask id="cam-hole">
                <rect width="300" height="400" fill="white" />
                <ellipse cx="150" cy="185" rx="82" ry="108" fill="black" />
              </mask>
            </defs>
            <rect width="300" height="400" fill="rgba(20,26,23,.45)" mask="url(#cam-hole)" />
            <ellipse
              cx="150"
              cy="185"
              rx="82"
              ry="108"
              fill="none"
              stroke="rgba(255,255,255,.85)"
              stroke-width="2"
              stroke-dasharray="6 6"
            />
          </svg>
        )}
        {ready && <p class="camera-hint">脸放进框里，露出额头和发际线</p>}
        {count !== null && <div class="camera-count">{count}</div>}
        {flash && <div class="camera-flash" onAnimationEnd={() => setFlash(false)} />}
        {!ready && !error && <p class="camera-status">正在打开摄像头…</p>}
        {error && (
          <div class="camera-error">
            <p>{error}</p>
            <button class="btn primary" onClick={onFallback}>
              <Icon name="image" size={18} /> 改用相册
            </button>
          </div>
        )}
      </div>

      <div class="camera-bar">
        <button class="camera-side" onClick={onClose}>
          取消
        </button>
        <button class="shutter" onClick={shoot} disabled={!ready} aria-label="拍照">
          <span />
        </button>
        <div class="camera-side right">
          <button class={`camera-mini ${timer ? "on" : ""}`} onClick={() => setTimer(!timer)} aria-label="倒计时">
            3s
          </button>
          {multi && (
            <button
              class="camera-mini"
              onClick={() => setFacing(facing === "user" ? "environment" : "user")}
              aria-label="切换摄像头"
            >
              <Icon name="refresh" size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
